import React, { memo, useEffect, useState, useCallback } from "react";
import {
  Star,
  Clock,
  Wifi,
  Car,
  Utensils,
  Dumbbell,
  Shield,
  ChevronRight,
  Check,
  Users,
  Building,
  ChevronLeft,
  X,
  Sparkles,
  DollarSign,
  BadgeCheck
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getOffMarketSpecialCommissionBreakdown } from "../../utils/offMarketSpecialCommission";

const HotelHero = memo(({ hotelData, hotelMedia, hotelContent, hotelDescriptive, contentData, mediaData, isOffMarketStay = false, offMarketProperty = null }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentGalleryImageIndex, setCurrentGalleryImageIndex] = useState(0);


  // Extract hotel information - use AXLEMS-compatible structure if available, fallback to our structure
  const hotelInfo = contentData?.HotelInfo || hotelData?.HotelInfo || contentData?.HotelDescriptiveInfo?.HotelInfo || hotelData?.HotelDescriptiveInfo?.HotelInfo || hotelData?.hotelInfo || {};
  const locationInfo = contentData?.HotelDescriptiveInfo?.LocationInfo || hotelData?.HotelDescriptiveInfo?.LocationInfo || hotelDescriptive?.LocationInfo || {};
  const propertyInfo = contentData?.HotelDescriptiveInfo?.PropertyInfo || hotelData?.HotelDescriptiveInfo?.PropertyInfo || hotelDescriptive?.PropertyInfo || {};
  const amenities = contentData?.HotelDescriptiveInfo?.Amenities?.Amenity || hotelData?.HotelDescriptiveInfo?.Amenities?.Amenity || hotelDescriptive?.Amenities?.Amenity || [];
  const securityFeatures = contentData?.HotelDescriptiveInfo?.SecurityFeatures?.SecurityFeature || hotelData?.HotelDescriptiveInfo?.SecurityFeatures?.SecurityFeature || hotelDescriptive?.SecurityFeatures?.SecurityFeature || [];

  
  // Extract descriptions
  const descriptions = (contentData?.HotelDescriptiveInfo?.Descriptions?.Description || hotelData?.HotelDescriptiveInfo?.Descriptions?.Description || hotelDescriptive?.Descriptions?.Description || []).reduce((acc, desc) => {
    if (desc.Text) {
      const type = desc.Text.Type;
      const value = desc.Text.value;
      if (type && value) {
        acc[type] = value;
      }
    }
    return acc;
  }, {}) || {};

  // Extract images from media data - try multiple sources
  const images = hotelData?.images || hotelMedia?.HotelMedia?.Media || [];
  const hotelImages = images.filter(img => 
    (img.Type === 'IMAGE' && img.Url) || // Sabre structure
    (img.url) || // Our standardized structure
    (img.imageUrl) || // Alternative structure
    (img.urls && (img.urls.original || img.urls.medium || img.urls.small)) // Our backend structure
  );



  // Hotel basic info - try multiple paths like the reference code
  const hotelName = hotelInfo.HotelName || hotelInfo.Name || hotelData?.hotelInfo?.name || hotelData?.HotelInfo?.HotelName || hotelData?.HotelInfo?.Name || 'Hotel Name';
  
  const hotelAddress = hotelInfo.address?.addressLines || locationInfo.Address?.AddressLine || hotelData?.Address?.AddressLine || [];
  const city = hotelInfo.address?.city?.value || hotelInfo.address?.city || locationInfo.Address?.CityName?.value || locationInfo.Address?.CityName || hotelData?.Address?.CityName?.value || hotelData?.Address?.CityName || '';
  const state = hotelInfo.address?.state?.value || hotelInfo.address?.state || locationInfo.Address?.StateProv?.StateCode || hotelData?.Address?.StateProv?.StateCode || '';
  const country = hotelInfo.address?.country?.value || hotelInfo.address?.country || locationInfo.Address?.CountryName?.value || locationInfo.Address?.CountryName || hotelData?.Address?.CountryName?.value || hotelData?.Address?.CountryName || '';
  const phone = hotelInfo.contact?.phone || locationInfo.Contact?.Phone || hotelData?.Contact?.Phone || '';
  
  // Get rating from multiple sources - prioritize SabreRating
  const rating = parseFloat(hotelInfo.SabreRating) || parseFloat(hotelInfo.Rating) || parseFloat(hotelData?.Rating) || 0;
  const starRating = Math.round(rating);
  
  // Get hotel description from descriptions
 const hotelDescription = isOffMarketStay && offMarketProperty?.shortDescription 
    ? offMarketProperty.shortDescription 
    : (descriptions.ShortDescription || descriptions.General || descriptions.Description || '');
  const hasOffMarketPerks = Boolean(
    offMarketProperty?.axonallPerks?.enabled &&
    Array.isArray(offMarketProperty?.axonallPerks?.items) &&
    offMarketProperty.axonallPerks.items.length
  );
  const perksPreview = hasOffMarketPerks ? offMarketProperty.axonallPerks.items.slice(0, 2) : [];
  const hasOffMarketCommission = Boolean(
    offMarketProperty?.specialCommission?.enabled &&
    offMarketProperty?.specialCommission?.percentage != null
  );
  const commissionBreakdown = hasOffMarketCommission
    ? getOffMarketSpecialCommissionBreakdown(offMarketProperty.specialCommission)
    : null;
  // Property details
  const checkInTime = propertyInfo.CheckInTime || '15:00';
  const checkOutTime = propertyInfo.CheckOutTime || '11:00';
  const totalRooms = propertyInfo.TotalRooms || 0;
  const floors = propertyInfo.Floors || 0;


  // Key amenities (first 6)
  const keyAmenities = amenities.slice(0, 6).map(amenity => ({
    name: amenity.Description || amenity,
    icon: getAmenityIcon(amenity.Description || amenity)
  }));

  // All amenities for expanded view
  const allAmenities = amenities.map(amenity => ({
    name: amenity.Description || amenity,
    icon: getAmenityIcon(amenity.Description || amenity)
  }));



  // Navigation functions
  const nextImage = useCallback(() => {
    if (hotelImages.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev + 1) % hotelImages.length);
      setIsTransitioning(false);
    }, 150);
  }, [hotelImages.length]);

  const prevImage = useCallback(() => {
    if (hotelImages.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev - 1 + hotelImages.length) % hotelImages.length);
      setIsTransitioning(false);
    }, 150);
  }, [hotelImages.length]);

  const openImageModal = useCallback((index) => {
    setCurrentGalleryImageIndex(index);
    setImageModalOpen(true);
  }, []);

  const closeImageModal = useCallback(() => {
    setImageModalOpen(false);
  }, []);

  // Auto-advance images
  useEffect(() => {
    if (hotelImages.length <= 1) return;
    
    const interval = setInterval(() => {
      nextImage();
    }, 5000);

    return () => clearInterval(interval);
  }, [hotelImages.length, nextImage]);

  // Render star rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <>
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="relative bg-white">
        {/* Hero Image Section */}
        <div className="relative w-full overflow-hidden min-h-[52vh] h-[58dvh] max-h-[820px] md:min-h-0 md:h-screen md:max-h-none">
          {hotelImages.length > 0 ? (
            <>
              <motion.div
                key={currentImageIndex}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="absolute inset-0"
              >
                <img
                  src={hotelImages[currentImageIndex]?.urls?.original || hotelImages[currentImageIndex]?.urls?.large || hotelImages[currentImageIndex]?.urls?.medium || hotelImages[currentImageIndex]?.Url || hotelImages[currentImageIndex]?.url || hotelImages[currentImageIndex]?.imageUrl}
                  alt={`${hotelName} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgdmlld0JveD0iMCAwIDgwMCA2MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI4MDAiIGhlaWdodD0iNjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zNTAgMjUwSDQ1MFYzNTBIMzUwVjI1MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHN2ZyB4PSIzNzAiIHk9IjI3MCIgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9Im5vbmUiPgo8cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJTNi40OCAyMiAxMiAyMlMyMiAxNy41MiAyMiAxMlMxNy41MiAyIDEyIDJaTTEzIDE3SDExVjE1SDEzVjE3Wk0xMyAxM0gxMVY3SDEzVjEzWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4KPC9zdmc+';
                  }}
                />
                <div className="absolute inset-0 bg-black/30" />
              </motion.div>

              {hotelImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 bg-black/35 hover:bg-black/50 text-white p-2 sm:p-3 rounded-full transition-all backdrop-blur-sm"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 bg-black/35 hover:bg-black/50 text-white p-2 sm:p-3 rounded-full transition-all backdrop-blur-sm"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </>
              )}

              {/* Gallery Thumbnails */}
              {hotelImages.length > 1 && (
                <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[100vw] px-2 sm:px-4">
                  <div className="flex gap-2 sm:gap-3 max-w-lg mx-auto overflow-x-auto scrollbar-hide pb-1">
                    {hotelImages.slice(0, 6).map((image, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentImageIndex(index);
                          setIsTransitioning(true);
                          setTimeout(() => setIsTransitioning(false), 150);
                        }}
                        className={`relative flex-shrink-0 w-16 h-11 sm:w-20 sm:h-14 rounded-lg overflow-hidden transition-all duration-300 shadow-lg ${
                          index === currentImageIndex
                            ? 'ring-2 ring-white/80 scale-105'
                            : 'opacity-60 hover:opacity-90 hover:scale-105'
                        }`}
                      >
                        <img
                          src={image.urls?.large || image.urls?.medium || image.urls?.small || image.Url || image.url || image.imageUrl}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium" style={{display: 'none'}}>
                          No Image
                        </div>
                        {index === currentImageIndex && (
                          <div className="absolute inset-0 bg-white/10" />
                        )}
                      </button>
                    ))}
                    {hotelImages.length > 6 && (
                      <button
                        onClick={() => openImageModal(0)}
                        className="flex-shrink-0 w-16 h-11 sm:w-20 sm:h-14 rounded-lg border-2 border-white/50 flex items-center justify-center text-white/80 hover:text-white hover:border-white/80 transition-all duration-300 hover:scale-105 text-sm"
                      >
                        +{hotelImages.length - 6}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <Building className="w-16 h-16 mx-auto mb-4" />
                <p className="text-lg font-medium">No images available</p>
              </div>
            </div>
          )}

          {/* Hotel Info Overlay - Centered */}
          <div className="absolute inset-0 flex items-center justify-center text-white px-3 sm:px-6 pb-24 sm:pb-16 md:pb-0">
            <div className="text-center max-w-4xl w-full">
              {/* City above hotel name */}
              <div className="mb-2 sm:mb-4">
                <span className="text-white/80 text-xs sm:text-sm font-medium tracking-widest uppercase">
                  {city}
                </span>
              </div>
              
              {/* Hotel name */}
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-normal mb-3 sm:mb-4 text-white font-AXONALLPrimeRegular leading-tight break-words hyphens-auto">
                {hotelName}
              </h1>
              
              {/* Rating and description */}
              <div className="mb-6">
                <div className="flex items-center justify-center mb-4">
                  <div className="flex items-center gap-1">
                    {renderStars(starRating)}
                  </div>
                </div>
                
                {/* Hotel description */}
                {hotelDescription && (
                  <div className="max-w-3xl mx-auto">
                    <p className="text-white/90 text-sm sm:text-base md:text-lg leading-relaxed font-poppinsLight line-clamp-4 sm:line-clamp-none">
                      {hotelDescription.length > 200 
                        ? `${hotelDescription.substring(0, 200)}...` 
                        : hotelDescription
                      }
                    </p>
                  </div>
                )}

                {/* Off-market highlight strip in hero */}
                {isOffMarketStay && (
                  <div className="max-w-3xl mx-auto mt-5">
                    <div className="rounded-2xl border border-white/15 bg-black/55 backdrop-blur-xl p-4 sm:p-5 shadow-2xl">
                      <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-[10px] sm:text-xs font-semibold uppercase tracking-[0.16em]">
                          <BadgeCheck className="w-3.5 h-3.5" />
                          Off-Market Stay
                        </span>
                        {hasOffMarketCommission && commissionBreakdown && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/30 text-white text-[10px] sm:text-xs font-semibold uppercase tracking-[0.16em]">
                            <DollarSign className="w-3.5 h-3.5" />
                            {commissionBreakdown.specialAdded > 0
                              ? `Commission ${commissionBreakdown.total}% (${commissionBreakdown.defaultBase}% + ${commissionBreakdown.specialAdded}%)`
                              : `Commission ${commissionBreakdown.total}%`}
                          </span>
                        )}
                        {hasOffMarketPerks && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-[10px] sm:text-xs font-semibold uppercase tracking-[0.16em]">
                            <Sparkles className="w-3.5 h-3.5" />
                            Axonall Perks
                          </span>
                        )}
                      </div>
                      {hasOffMarketPerks && (
                        <div className="mt-3.5 text-center max-w-2xl mx-auto rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
                          <p className="text-[11px] sm:text-xs text-white/70 font-medium mb-1.5 uppercase tracking-[0.14em]">
                            Included perks
                          </p>
                          <ul className="space-y-1">
                            {perksPreview.map((perk, idx) => (
                              <li key={`${perk.title}-${idx}`} className="text-xs sm:text-sm text-white/90 leading-relaxed">
                                <span className="text-white/70 mr-1.5">•</span>
                                <span className="font-semibold">{perk.title}</span>
                                {perk.description ? <span className="text-white/70"> — {perk.description}</span> : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Hotel Details Section */}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-6 bg-white">
          {/* Hotel Description Section */}
          {hotelDescription && (
            <div className="mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-2xl font-bold mb-4 text-neutral-900 typography-subsection-title">
                  About This Hotel
                </h3>
                <div className="prose prose-neutral max-w-none">
                  <p className="text-neutral-700 leading-relaxed text-base font-poppinsLight">
                    {hotelDescription}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Top Row - Property Info & Security Combined */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {/* Property Information - Horizontal Layout */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold mb-4 text-neutral-900 typography-subsection-title">
                  Property Information
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200/80">
                    <div className="w-10 h-10 bg-slate-200/80 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-slate-700" />
                    </div>
                    <div>
                      <p className="text-xs font-poppinsMedium text-slate-600 uppercase tracking-wide">Check-in</p>
                      <p className="text-lg font-poppinsSemiBold text-slate-900">{checkInTime}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200/80">
                    <div className="w-10 h-10 bg-slate-200/80 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-slate-700" />
                    </div>
                    <div>
                      <p className="text-xs font-poppinsMedium text-slate-600 uppercase tracking-wide">Check-out</p>
                      <p className="text-lg font-poppinsSemiBold text-slate-900">{checkOutTime}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200/80">
                    <div className="w-10 h-10 bg-slate-200/80 rounded-lg flex items-center justify-center">
                      <Building className="w-5 h-5 text-slate-700" />
                    </div>
                    <div>
                      <p className="text-xs font-poppinsMedium text-slate-600 uppercase tracking-wide">Floors</p>
                      <p className="text-lg font-poppinsSemiBold text-slate-900">{floors}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security & Safety - Compact Sidebar */}
            {securityFeatures.length > 0 && (
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
                  <h3 className="text-lg font-bold mb-4 text-neutral-900 typography-subsection-title">
                    Security & Safety
                  </h3>
                  
                  <div className="space-y-3">
                    {securityFeatures.slice(0, 3).map((feature, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200/80">
                        <div className="w-8 h-8 bg-slate-200/80 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Shield className="w-4 h-4 text-slate-700" />
                        </div>
                        <span className="text-sm font-poppinsMedium text-slate-700 leading-relaxed">{feature.Description || feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Row - Key Amenities Full Width */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-light mb-4 text-neutral-900 typography-subsection-title">
              Key Amenities
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {keyAmenities.map((amenity, index) => (
                <div key={index} className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 text-center">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                    {amenity.icon}
                  </div>
                  <span className="text-xs font-poppinsMedium text-gray-700 leading-tight">{amenity.name}</span>
                </div>
              ))}
            </div>

            {allAmenities.length > 6 && (
              <button
                onClick={() => setShowAllAmenities(!showAllAmenities)}
                className="mt-6 w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {showAllAmenities ? 'Show Less' : `Show All ${allAmenities.length} Amenities`}
                <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${showAllAmenities ? 'rotate-90' : ''}`} />
              </button>
            )}

            <AnimatePresence>
              {showAllAmenities && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="mt-6 overflow-hidden"
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {allAmenities.slice(6).map((amenity, index) => (
                      <div key={index + 6} className="flex flex-col items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 text-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                          {amenity.icon}
                        </div>
                        <span className="text-xs font-poppinsMedium text-gray-700 leading-tight">{amenity.name}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Image Gallery Modal */}
        <AnimatePresence>
          {imageModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-2 sm:p-4"
              onClick={closeImageModal}
            >
              <div className="relative max-w-6xl max-h-full w-full flex items-center justify-center">
                <button
                  onClick={closeImageModal}
                  className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full transition-all duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
                
                <img
                  src={hotelImages[currentGalleryImageIndex]?.urls?.original || hotelImages[currentGalleryImageIndex]?.urls?.medium || hotelImages[currentGalleryImageIndex]?.Url || hotelImages[currentGalleryImageIndex]?.url || hotelImages[currentGalleryImageIndex]?.imageUrl}
                  alt={`Gallery ${currentGalleryImageIndex + 1}`}
                  className="max-w-full max-h-[75dvh] sm:max-h-[85vh] object-contain rounded-lg shadow-xl"
                />
                
                {hotelImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentGalleryImageIndex((prev) => (prev - 1 + hotelImages.length) % hotelImages.length);
                      }}
                      className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full transition-all duration-200"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentGalleryImageIndex((prev) => (prev + 1) % hotelImages.length);
                      }}
                      className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 sm:p-3 rounded-full transition-all duration-200"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
                
                <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 bg-white/90 text-gray-800 px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                  {currentGalleryImageIndex + 1} / {hotelImages.length}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
});

// Helper function to get amenity icons
function getAmenityIcon(amenityName) {
  const name = amenityName.toLowerCase();
  const iconCls = 'w-5 h-5 text-slate-700';

  if (name.includes('wifi') || name.includes('internet')) {
    return <Wifi className={iconCls} />;
  } else if (name.includes('parking') || name.includes('car')) {
    return <Car className={iconCls} />;
  } else if (name.includes('restaurant') || name.includes('dining') || name.includes('food')) {
    return <Utensils className={iconCls} />;
  } else if (name.includes('gym') || name.includes('fitness') || name.includes('exercise')) {
    return <Dumbbell className={iconCls} />;
  } else if (name.includes('pool') || name.includes('swimming')) {
    return <div className="w-5 h-5 bg-slate-600 rounded-full flex items-center justify-center">
      <span className="text-white text-xs font-bold">P</span>
    </div>;
  } else if (name.includes('spa') || name.includes('wellness')) {
    return <div className="w-5 h-5 bg-slate-600 rounded-full flex items-center justify-center">
      <span className="text-white text-xs font-bold">S</span>
    </div>;
  } else if (name.includes('security') || name.includes('safety')) {
    return <Shield className={iconCls} />;
  } else if (name.includes('concierge') || name.includes('service')) {
    return <Users className={iconCls} />;
  } else {
    return <Check className={iconCls} />;
  }
}

HotelHero.displayName = 'HotelHero';

export default HotelHero;
